import { graphql } from "@acto/gql";
import { useMemo } from "react";
import { PaginatedDataAccessResult } from "@acto/taskpane/core/data-access/types";
import { usePagination } from "@acto/taskpane/core/data-access/pagination/usePagination";
import { ProductExclusion } from "@acto/taskpane/core/domain";

const ProductExclusionFeedQuery = graphql(`
  query GetProductExclusionFeed($first: Int!, $after: String) {
    productExclusions(first: $first, after: $after) {
      __typename
      edges {
        __typename
        node {
          id
          __typename
          clientId
          productId
          productName {
            locale
            value
          }
          reason
          reasonText
          validTo
          client {
            __typename
            id
            name
            externalReference
          }
        }
      }
      pageInfo {
        __typename
        hasNextPage
        endCursor
      }
    }
  }
`);

export interface ClientProductExclusion {
  id: string;
  exclusion: ProductExclusion;
  clientName: string;
  clientReference: string;
}

export function useProductExclusionFeed({
  limit = 100,
  enabled = true,
}: { limit?: number; enabled?: boolean } = {}): PaginatedDataAccessResult<ClientProductExclusion> {
  const { data, isFetching, isPending, error, hasNextPage, fetchNextPage, refreshAfter } = usePagination({
    query: ProductExclusionFeedQuery,
    limit,
    enabled,
    connection: (data) => data.productExclusions,
  });

  const rows = useMemo(
    () =>
      data?.map((node) => ({
        id: node.id,
        exclusion: ProductExclusion.fromGraphql(node),
        clientName: node.client.name,
        clientReference: node.client.externalReference,
      })),
    [data],
  );

  return { data: rows, isFetching, isPending, error, hasNextPage, fetchNextPage, refreshAfter };
}
